import React, { useMemo, useState } from 'react';
import { ContextMenuContainer, ContextMenuProps } from './ContextMenuContainer';
import { ContextMenuItem } from './ContextMenuItem';
import { useReactFlow } from '@xyflow/react';
import { getDescriptionVersionIdByValueDate, OutputNodeAction } from './utils';
import translations from './OutputDataContextMenu.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { RelationNodeType } from './RelationNode';
import { path } from '../../paths';
import {
  DataObjectRelationNodeType,
  Frequency,
  getDayjs,
  valueDate as getValueDate,
  ISO_DATE_FORMAT,
  ISODate,
  getLatestOutputVersionId,
  DataObjectOutputType,
} from '@atlas-frontend/sdk';
import { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import UriTemplate from 'uri-template-lite';
import {
  RelationsTablesModal,
  RelationsTablesModalParams,
} from './RelationsTablesModal';
import {
  OutputDataActionType,
  OutputDataModalAction,
} from '../output-data/OutputDataDirectory';
import { renderOutputDataActionModal } from '../output-data/actionModals/renderOutputDataActionModal';

const { addTranslations, useTranslation } = i18nNsFactory(
  'relation-input-context-menu',
);
addTranslations(translations);

type OutputDataContextMenuProps = ContextMenuProps & {
  nodeId: string;
  onClose?: () => void;
  refetchGraphData: () => void;
};

export const OutputDataContextMenu: React.FC<OutputDataContextMenuProps> = ({
  nodeId,
  top,
  left,
  onClose,
  refetchGraphData,
}) => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { getNode } = useReactFlow();
  const [activeOutputInfoParams, setActiveOutputInfoParams] = useState<
    OutputDataModalAction | undefined
  >(undefined);
  const [relationsTablesParams, setRelationsTablesParams] = useState<
    RelationsTablesModalParams | undefined
  >(undefined);

  const node = getNode(nodeId) as unknown as RelationNodeType;

  const handleNodeAction = (
    action: OutputNodeAction | OutputDataActionType,
  ) => {
    if (!node) return;

    const {
      outputType,
      descriptionId,
      groupId,
      activePeriods = [],
      frequency,
      childNodes,
      dataObjectVersions,
      nodeType,
      dataObjectType,
      isRootNode,
      hasActiveProcessing,
      calculationErrors,
      hasAccess,
    } = node?.data ?? {};

    const groupOrDescriptionId =
      (nodeType !== DataObjectRelationNodeType.GROUP
        ? `${groupId}-${descriptionId}`
        : groupId) || undefined;

    const allDataObjectVersions = [
      ...(dataObjectVersions ?? []),
      ...(childNodes ?? []).flatMap(
        (childNode) => childNode?.dataObjectVersions ?? [],
      ),
    ];

    const validRanges = activePeriods?.map(
      (period) =>
        [
          getDayjs(period.validFrom) || null,
          getDayjs(period.validTo) || null,
        ] as [Dayjs | null, Dayjs | null],
    );

    const valueDateFromGraphFilter = searchParams.get('valueDate');

    const latestValueDate = (
      !isRootNode
        ? (getValueDate(
            getDayjs(node.data?.latestValueDate),
            (frequency?.code as Frequency) || 'YEAR',
            validRanges,
          )?.format(ISO_DATE_FORMAT) ?? valueDateFromGraphFilter)
        : valueDateFromGraphFilter
    ) as ISODate;

    const latestVersionWithLatestValueDate = getLatestOutputVersionId(
      allDataObjectVersions,
      latestValueDate || undefined,
    );
    const descriptionVersionId = getDescriptionVersionIdByValueDate(
      valueDateFromGraphFilter,
      activePeriods,
    );

    const nodeHasActiveProcessing = !!(
      hasActiveProcessing ||
      (childNodes &&
        childNodes.length > 0 &&
        childNodes.some((c) => c?.hasActiveProcessing))
    );

    const nodeHasCalculationErrors = !!(
      calculationErrors ||
      (childNodes &&
        childNodes.length > 0 &&
        childNodes.some((c) => c?.calculationErrors))
    );

    const isGroupNode = nodeType === DataObjectRelationNodeType.GROUP;

    switch (action) {
      case OutputNodeAction.OpenOutputData: {
        const url = new UriTemplate(
          `${path.visualValidationCrossTable}{?groupDescriptionRef,valueDate}`,
        ).expand({
          groupDescriptionRef: groupOrDescriptionId,
          valueDate: latestValueDate,
        });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case OutputNodeAction.OpenOutputDescription: {
        const url = new UriTemplate(
          path.outputDataObjectView.replace(':id', '{id}'),
        ).expand({ id: descriptionVersionId || '' });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case OutputNodeAction.OpenRelationsGraph: {
        const url = new UriTemplate(
          `${path.relations}{?type,groupOrDescriptionId,valueDate,version,showOneStep}`,
        ).expand({
          type: outputType || 'OUTPUT',
          groupOrDescriptionId,
          valueDate: latestValueDate,
          version: isGroupNode
            ? 'latest'
            : latestVersionWithLatestValueDate || undefined,
          showOneStep: true,
        });
        window.open(url, '_blank');
        onClose?.();
        break;
      }
      case OutputNodeAction.OpenRelationsTable:
        if (groupOrDescriptionId && latestValueDate) {
          setRelationsTablesParams({
            groupOrDescriptionId,
            valueDate: latestValueDate,
            version: isGroupNode
              ? 'latest'
              : latestVersionWithLatestValueDate || undefined,
            dataObjectType,
            isGroup: isGroupNode,
            rootNodeName: node.data.label,
          });
        }
        break;
      default:
        if (groupId && latestValueDate && frequency) {
          let selectAllOutputs;
          switch (action) {
            case OutputDataActionType.Calculate:
            case OutputDataActionType.CalculateAndValidate:
              selectAllOutputs = isGroupNode ? true : !nodeHasActiveProcessing;
              break;
            case OutputDataActionType.CancelCalculationErrors:
              selectAllOutputs = isGroupNode ? true : nodeHasCalculationErrors;
              break;
            default:
              selectAllOutputs = true;
          }

          setActiveOutputInfoParams({
            type: action,
            params: {
              groupId,
              valueDate: latestValueDate,
              frequencyId: frequency.id,
              selectAll: selectAllOutputs,
              hasAccess,
            },
          });
        }
    }
  };

  const actions = useMemo<
    (OutputNodeAction | OutputDataActionType | 'divider')[]
  >(() => {
    const { isRootNode, nodeType, childNodes, outputType } = node?.data ?? {};
    const isGroupNode = nodeType === DataObjectRelationNodeType.GROUP;
    const children = childNodes ?? [];

    const isReport = outputType === DataObjectOutputType.REPORT;
    const containsOnlyReports =
      children.length > 0 &&
      children.every(
        (childNode) => childNode?.outputType === DataObjectOutputType.REPORT,
      );
    const isIntermediate = outputType === DataObjectOutputType.INTERMEDIATE;
    const containsOnlyIntermediates =
      children.length > 0 &&
      children.every(
        (c) => c?.outputType === DataObjectOutputType.INTERMEDIATE,
      );
    const containsIntermediates = isIntermediate || containsOnlyIntermediates;

    const nodeActions = [];
    if (isRootNode) {
      nodeActions.push(OutputDataActionType.Calculate);

      if (!containsIntermediates) {
        nodeActions.push(OutputDataActionType.Validate);
      }
      nodeActions.push(OutputDataActionType.CalculateAndValidate);
      nodeActions.push(OutputDataActionType.CancelCalculationErrors);
      nodeActions.push('divider');

      if (!containsIntermediates) {
        nodeActions.push(OutputDataActionType.Confirm);
        if (!isReport && !containsOnlyReports) {
          nodeActions.push(OutputDataActionType.ConfirmAndCalculate);
        }
        nodeActions.push(OutputDataActionType.ChangeConfirmStatus);
        nodeActions.push('divider');
      }

      if (!isGroupNode) {
        if (!containsIntermediates) {
          nodeActions.push(OutputNodeAction.OpenOutputData);
        }
        nodeActions.push(OutputNodeAction.OpenOutputDescription);
        nodeActions.push('divider');
      }
    }

    if (!isRootNode) nodeActions.push(OutputNodeAction.OpenRelationsGraph);
    nodeActions.push(OutputNodeAction.OpenRelationsTable);
    nodeActions.push(OutputDataActionType.ShowRelated);
    nodeActions.push(OutputDataActionType.ShowConfirmations);

    if (isRootNode) {
      nodeActions.push('divider');
      nodeActions.push(OutputDataActionType.Delete);
    }

    return nodeActions as (
      | OutputNodeAction
      | OutputDataActionType
      | 'divider'
    )[];
  }, [node]);

  return (
    <ContextMenuContainer top={top} left={left}>
      {actions.map((action, idx) =>
        action === 'divider' ? (
          <hr key={`divider-${idx}`} />
        ) : (
          <ContextMenuItem
            key={action}
            id={action}
            onClick={() => handleNodeAction(action)}
          >
            {t(`action.${action}`)}
          </ContextMenuItem>
        ),
      )}
      {activeOutputInfoParams &&
        renderOutputDataActionModal(activeOutputInfoParams, () => {
          onClose?.();
          setActiveOutputInfoParams(undefined);
          refetchGraphData();
        })}
      {relationsTablesParams && (
        <RelationsTablesModal
          params={relationsTablesParams}
          onClose={() => {
            onClose?.();
            setRelationsTablesParams(undefined);
          }}
        />
      )}
    </ContextMenuContainer>
  );
};
